import type { EnrollmentDocumentItem, EnrollmentStatusEntry, ParentStudentFinancialOverview } from '@monecolier/api-client';
import { CLASS_LEVEL_LABELS, type ClassLevel } from '@monecolier/types';
import type { StatusTagTone } from '@monecolier/ui';

// UX-38 — EXTRAIT de `Home.tsx` (comportement STRICTEMENT INCHANGÉ, refactor
// pur) pour être réutilisé TEL QUEL par la nouvelle page `Inscriptions.tsx`
// (`ChildCard`) — même précédent qu'`Avatar`/`StatusTag` (UX-11B/UX-23) :
// jamais une seconde logique de dérivation de statut réécrite pour un
// contexte "légèrement différent". Voir Home.tsx pour l'historique complet
// du commentaire d'origine.

export function levelLabel(level: string): string {
  return CLASS_LEVEL_LABELS[level as ClassLevel] ?? level;
}

export type CardStatus =
  | { kind: 'rejected'; reason: string | null }
  | { kind: 'cancelled' }
  | { kind: 'reenrollmentPendingPayment' }
  | { kind: 'waitlisted'; position: number | null }
  | { kind: 'documentsMissing'; missing: string[] }
  | { kind: 'pending' }
  | { kind: 'pendingPaymentRequired' }
  | { kind: 'paymentPending' }
  | { kind: 'enrolledUnknownBalance' }
  | { kind: 'enrolled' }
  | { kind: 'loading' };

export type DocumentsFetchState = EnrollmentDocumentItem[] | 'error' | undefined;

export function missingRequiredDocuments(state: DocumentsFetchState): string[] | 'unknown' {
  if (state === undefined || state === 'error') return 'unknown';
  return state.filter((item) => item.requirement.required && item.document?.status !== 'VALIDATED').map((item) => item.requirement.name);
}

export function deriveCardStatus(
  entry: EnrollmentStatusEntry,
  documentsState: DocumentsFetchState,
  financial: ParentStudentFinancialOverview | null,
  financialSettled: boolean,
): CardStatus {
  if (entry.status === 'REJECTED') return { kind: 'rejected', reason: entry.decisionReason };
  if (entry.status === 'CANCELLED') return { kind: 'cancelled' };
  if (entry.status === 'REENROLLMENT_PENDING_PAYMENT') return { kind: 'reenrollmentPendingPayment' };
  if (entry.status === 'WAITLISTED') return { kind: 'waitlisted', position: entry.waitlistPosition };

  // ENR-22 — bug confirmé : la facture d'inscription (ENR-01) existe DÈS la
  // soumission, avant même toute décision de l'école — un solde dû sur une
  // demande encore PENDING prime donc explicitement sur la vérification des
  // documents ci-dessous, jamais masqué derrière elle (contrairement à ce qui
  // se serait produit si ce contrôle vivait après le `missingRequiredDocuments`
  // partagé). Le lien "Documents à fournir" reste, LUI, INCONDITIONNEL pour
  // tout PENDING (voir Home.tsx/ChildCard.tsx, jamais dérivé de
  // `CardStatus.kind`) — c'est ce qui permet aux deux actions de coexister
  // sans avoir à encoder deux signaux simultanés dans un seul `kind`.
  if (entry.status === 'PENDING' && financial) {
    const outstanding = Number(financial.totalOutstanding);
    if (Number.isFinite(outstanding) && outstanding > 0) return { kind: 'pendingPaymentRequired' };
  }

  // PENDING (sans solde dû) ou APPROVED uniquement à partir d'ici — seuls
  // statuts pour lesquels l'état des documents change ce qui est affiché.
  const missing = missingRequiredDocuments(documentsState);
  if (missing === 'unknown') return { kind: 'loading' };
  if (missing.length > 0) return { kind: 'documentsMissing', missing };

  if (entry.status === 'PENDING') return { kind: 'pending' };

  // APPROVED, dossier complet — reste à savoir si la scolarité est à jour.
  if (!financial) return financialSettled ? { kind: 'enrolledUnknownBalance' } : { kind: 'loading' };
  const outstanding = Number(financial.totalOutstanding);
  if (Number.isFinite(outstanding) && outstanding > 0) return { kind: 'paymentPending' };
  return { kind: 'enrolled' };
}

// ⚠️ UX-40 — RÈGLE SÉMANTIQUE DU MAPPING STATUT → COULEUR, à respecter pour
// TOUT nouveau statut ajouté à `CardStatus` (documentée ICI précisément pour
// éviter une nouvelle dérive : avant ce ticket, les tons gris/ambre/bleu
// étaient distribués de façon ad hoc entre les statuts intermédiaires) :
//
//   - **vert**  = positif DÉFINITIF (l'inscription est acquise)
//   - **rouge** = négatif DÉFINITIF (l'inscription n'aura pas lieu)
//   - **gris**  = attente PASSIVE (personne n'a rien à faire, ça suit son cours)
//   - **ambre** = ACTION REQUISE de quelqu'un (le parent OU l'école)
//
// La teinte `blue` est délibérément ABSENTE de ce mapping depuis UX-40 : elle
// ne correspond à aucune de ces quatre catégories (elle servait indistinctement
// à deux états "paiement requis", désormais ambre). Elle reste disponible dans
// `StatusTagTone` pour d'AUTRES domaines — notamment
// `Payments.tsx#INVOICE_STATUS_TONE`, qui colore des statuts de FACTURE
// (`PARTIALLY_PAID`), jamais des statuts d'inscription : contexte différent,
// délibérément NON aligné par ce ticket.
//
// ⚠️ SOURCE DE VÉRITÉ UNIQUE : le badge (`EnrollmentStatusBadge`), la bordure
// gauche de la carte (`CARD_ACCENT_CLASSNAMES`) et l'avatar à initiales lisent
// TOUS ce même objet — jamais trois listes parallèles qui pourraient
// re-diverger, ce qui est exactement la dérive que ce ticket corrige.
export const CARD_TONE_BY_STATUS: Record<CardStatus['kind'], StatusTagTone> = {
  // Négatif définitif — l'inscription n'aura pas lieu, dans les deux cas.
  rejected: 'red',
  cancelled: 'red',
  // Action requise : régler la facture d'inscription (ENR-01/ENR-13) ou le
  // solde de scolarité restant dû.
  pendingPaymentRequired: 'amber',
  reenrollmentPendingPayment: 'amber',
  paymentPending: 'amber',
  // Action requise : déposer un document obligatoire manquant (ENR-07).
  documentsMissing: 'amber',
  // Action requise pour débloquer une place (côté école) — un dossier bloqué
  // par une classe pleine n'avance pas tout seul, contrairement à `pending`.
  waitlisted: 'amber',
  // Attente passive — dossier complet, la demande suit son cours côté école.
  pending: 'gray',
  // Attente passive — état transitoire de chargement, jamais un vrai statut.
  loading: 'gray',
  // Positif définitif.
  enrolledUnknownBalance: 'green',
  enrolled: 'green',
};

// Une SEULE classe d'accent par ton (avant UX-40, `amber-400`/`amber-500` et
// `gray-200`/`gray-300`/`gray-400` coexistaient sans raison documentée) — la
// bordure gauche ne peut plus diverger du badge affiché juste à côté.
const ACCENT_CLASSNAME_BY_TONE: Record<StatusTagTone, string> = {
  green: 'border-l-4 border-l-green-500',
  red: 'border-l-4 border-l-red-400',
  // UX-64 : `amber-600` (accent Figma, #D97706) — remplace `amber-500`, ce
  // filet décoratif de 4px ne porte jamais de texte (voir docs/history/UX.md "UX-64").
  amber: 'border-l-4 border-l-amber-600',
  gray: 'border-l-4 border-l-gray-400',
  // Jamais atteints par `CARD_TONE_BY_STATUS` ci-dessus (voir la règle) —
  // présents uniquement pour que ce `Record` reste exhaustif.
  blue: 'border-l-4 border-l-blue-400',
  brand: 'border-l-4 border-l-brand-500',
};

export const CARD_ACCENT_CLASSNAMES = Object.fromEntries(
  Object.entries(CARD_TONE_BY_STATUS).map(([kind, tone]) => [kind, ACCENT_CLASSNAME_BY_TONE[tone]]),
) as Record<CardStatus['kind'], string>;
